<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';
import {
  normalizeSpaceField,
  normalizeStandardBookingField,
} from 'dashboard/helper/preChat';
import Draggable from 'vuedraggable';
import NextButton from 'dashboard/components-next/button/Button.vue';
import FieldCard from './FieldCard.vue';

const props = defineProps({
  preChatFields: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['addField', 'createAttribute']);

const { t } = useI18n();
const getters = useStoreGetters();

const searchQuery = ref('');

const allAttributes = computed(() => getters['attributes/getAttributes'].value);

const enabledFieldNames = computed(
  () => new Set(props.preChatFields.filter(f => f.enabled).map(f => f.name))
);

// Standard: contact basics + conversation Spaces
const standardFields = computed(() =>
  props.preChatFields.filter(f => {
    if (f.field_type !== 'standard' || f.name === 'message') return false;
    // Booking date/time fields live under Event attributes
    if (
      f.name === 'pax' ||
      f.name === 'booking_datetime' ||
      f.type === 'booking_datetime'
    ) {
      return false;
    }
    return true;
  })
);

// Booking fields shown under Event attributes (incl. event Spaces for allocations)
const bookingEventFields = computed(() =>
  props.preChatFields
    .filter(
      f =>
        f.name === 'event_space_id' ||
        f.name === 'pax' ||
        f.name === 'booking_datetime' ||
        f.type === 'booking_datetime' ||
        (f.type === 'space' && f.field_type === 'event_attribute')
    )
    .map(f => normalizeStandardBookingField(f))
);

const contactAttributes = computed(() =>
  allAttributes.value.filter(a => a.attribute_model === 'contact_attribute')
);

const conversationAttributes = computed(() =>
  allAttributes.value.filter(
    a => a.attribute_model === 'conversation_attribute'
  )
);

const eventAttributes = computed(() =>
  allAttributes.value.filter(
    a =>
      a.attribute_model === 'event_attribute' &&
      a.attribute_key !== 'space_id' &&
      a.attribute_key !== 'spaces' &&
      a.attribute_key !== 'event_space_id' &&
      a.attribute_key !== 'pax' &&
      a.attribute_key !== 'booking_datetime'
  )
);

const toPickerField = attr => ({
  label: attr.attribute_display_name,
  name: attr.attribute_key,
  type: attr.attribute_display_type,
  field_type: attr.attribute_model,
  values: attr.attribute_values || [],
  regex_pattern: attr.regex_pattern,
  regex_cue: attr.regex_cue,
  required: false,
  enabled: false,
});

const filterBySearch = fields =>
  fields.filter(f => {
    const q = searchQuery.value.toLowerCase();
    if (!q) return true;
    const name = (f.label || f.name || '').toLowerCase();
    return name.includes(q);
  });

const filteredStandard = computed(() => filterBySearch(standardFields.value));

const filteredContact = computed(() =>
  filterBySearch(contactAttributes.value.map(toPickerField))
);

const filteredConversation = computed(() =>
  filterBySearch(conversationAttributes.value.map(toPickerField))
);

const filteredEvent = computed(() =>
  filterBySearch([
    ...bookingEventFields.value,
    ...eventAttributes.value.map(toPickerField),
  ])
);

const isFieldOnCanvas = name => enabledFieldNames.value.has(name);

const prepareFieldForCanvas = field =>
  normalizeSpaceField(
    normalizeStandardBookingField({
      ...field,
      enabled: true,
    })
  );

const handleAddField = field => {
  if (isFieldOnCanvas(field.name)) return;
  emit('addField', prepareFieldForCanvas(field));
};

// Clone function for draggable — produces a copy with enabled: true
const cloneField = field => prepareFieldForCanvas(field);

const canCloneToCanvas = evt => {
  const el = evt.draggedContext.element;
  if (!el) return false;
  // Never reorder within the picker; only allow clone onto canvas when not present
  if (evt.from === evt.to) return false;
  return !isFieldOnCanvas(el.name);
};

// Prevent the source list from mutating when a field is dragged out
const onPickerChange = () => {};
</script>

<template>
  <aside
    class="w-72 shrink-0 border-r border-n-weak bg-n-solid-2 flex flex-col overflow-hidden rounded-l-xl"
  >
    <div class="p-3 border-b border-n-weak">
      <div class="relative">
        <span
          class="i-lucide-search absolute left-2.5 top-1/2 -translate-y-1/2 text-n-slate-10 size-4"
        />
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.SEARCH_FIELDS')"
          class="reset-base w-full h-8 pl-8 pr-3 text-sm rounded-lg bg-n-alpha-1 outline outline-1 outline-n-container text-n-slate-10 placeholder:text-n-slate-10 focus:outline-n-brand transition-colors"
        />
      </div>
    </div>

    <div class="flex-1 overflow-y-auto">
      <!-- Standard Fields -->
      <div v-if="filteredStandard.length">
        <h4
          class="z-10 bg-white p-4 py-6 text-xs font-semibold uppercase tracking-wider text-n-slate-10 flex items-center gap-1.5 sticky top-0 border-b border-neutral-50 mb-3"
        >
          <span class="i-lucide-star size-4" />
          {{ t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.SECTIONS.STANDARD') }}
        </h4>
        <Draggable
          :list="filteredStandard"
          item-key="name"
          :group="{ name: 'pre-chat-fields', pull: 'clone', put: false }"
          :sort="false"
          :clone="cloneField"
          :move="canCloneToCanvas"
          class="flex flex-col gap-0.5 px-1"
          @change="onPickerChange"
        >
          <template #item="{ element: field }">
            <div
              :class="{
                'opacity-50 cursor-not-allowed': isFieldOnCanvas(field.name),
              }"
              @click="handleAddField(field)"
            >
              <FieldCard
                :field="field"
                compact
                :is-on-canvas="isFieldOnCanvas(field.name)"
              />
            </div>
          </template>
        </Draggable>
      </div>

      <!-- Contact Attributes -->
      <div v-if="filteredContact.length">
        <h4
          class="z-10 bg-white p-4 py-6 text-xs font-semibold uppercase tracking-wider text-n-slate-10 flex items-center gap-1.5 sticky top-0 border-b border-neutral-50 mb-3"
        >
          <span class="i-lucide-contact size-4" />
          {{ t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.SECTIONS.CONTACT') }}
        </h4>
        <Draggable
          :list="filteredContact"
          item-key="name"
          :group="{ name: 'pre-chat-fields', pull: 'clone', put: false }"
          :sort="false"
          :clone="cloneField"
          :move="canCloneToCanvas"
          class="flex flex-col gap-0.5 px-1"
          @change="onPickerChange"
        >
          <template #item="{ element: field }">
            <div
              :class="{
                'opacity-50 cursor-not-allowed': isFieldOnCanvas(field.name),
              }"
              @click="handleAddField(field)"
            >
              <FieldCard
                :field="field"
                compact
                :is-on-canvas="isFieldOnCanvas(field.name)"
              />
            </div>
          </template>
        </Draggable>
      </div>

      <!-- Conversation Attributes -->
      <div v-if="filteredConversation.length">
        <h4
          class="z-10 bg-white p-4 py-6 text-xs font-semibold uppercase tracking-wider text-n-slate-10 flex items-center gap-1.5 sticky top-0 border-b border-neutral-50 mb-3"
        >
          <span class="i-lucide-message-circle size-4" />
          {{ t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.SECTIONS.CONVERSATION') }}
        </h4>
        <Draggable
          :list="filteredConversation"
          item-key="name"
          :group="{ name: 'pre-chat-fields', pull: 'clone', put: false }"
          :sort="false"
          :clone="cloneField"
          :move="canCloneToCanvas"
          class="flex flex-col gap-0.5 px-1"
          @change="onPickerChange"
        >
          <template #item="{ element: field }">
            <div
              :class="{
                'opacity-50 cursor-not-allowed': isFieldOnCanvas(field.name),
              }"
              @click="handleAddField(field)"
            >
              <FieldCard
                :field="field"
                compact
                :is-on-canvas="isFieldOnCanvas(field.name)"
              />
            </div>
          </template>
        </Draggable>
      </div>

      <!-- Event Attributes -->
      <div v-if="filteredEvent.length">
        <h4
          class="z-10 bg-white p-4 py-6 text-xs font-semibold uppercase tracking-wider text-n-slate-10 flex items-center gap-1.5 sticky top-0 border-b border-neutral-50 mb-3"
        >
          <span class="i-lucide-calendar size-4" />
          {{ t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.SECTIONS.EVENT') }}
        </h4>
        <Draggable
          :list="filteredEvent"
          item-key="name"
          :group="{ name: 'pre-chat-fields', pull: 'clone', put: false }"
          :sort="false"
          :clone="cloneField"
          :move="canCloneToCanvas"
          class="flex flex-col gap-0.5 px-1"
          @change="onPickerChange"
        >
          <template #item="{ element: field }">
            <div
              :class="{
                'opacity-50 cursor-not-allowed': isFieldOnCanvas(field.name),
              }"
              @click="handleAddField(field)"
            >
              <FieldCard
                :field="field"
                compact
                :is-on-canvas="isFieldOnCanvas(field.name)"
              />
            </div>
          </template>
        </Draggable>
      </div>

      <div
        v-if="
          !filteredStandard.length &&
          !filteredContact.length &&
          !filteredConversation.length &&
          !filteredEvent.length
        "
        class="px-3 py-6 text-center text-sm text-n-slate-11"
      >
        {{ t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.NO_FIELDS_FOUND') }}
      </div>
    </div>

    <div class="p-3 border-t border-n-weak">
      <NextButton
        icon="i-lucide-plus"
        size="sm"
        variant="ghost"
        class="w-full"
        :label="t('INBOX_MGMT.PRE_CHAT_FORM.BUILDER.CREATE_ATTRIBUTE')"
        @click="emit('createAttribute')"
      />
    </div>
  </aside>
</template>
